import { useState, useCallback, useContext, ReactNode } from 'react'
import { useRouteMatch, useHistory } from 'react-router-dom'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { sliceUrl } from 'utils'
import isNil from 'lodash/isNil'
import { FormikContext } from 'formik'

export interface Tab {
  label: ReactNode
  path: string
  validatedFields?: string[]
  icon?: JSX.Element
}

interface TabsWithSecondaryTabs {
  tabs: string[] | undefined
  defaultSecondary: string | undefined
}

export type TabList = Tab[]

export type RoutedTabsOptions = {
  tabKey?: string
  tabsWithSecondaryTabs?: TabsWithSecondaryTabs
}

function hasTabs(tabs: TabList | undefined): tabs is Tab[] {
  return Array.isArray(tabs)
}

const SCOPETAB_KEY = 'scopetab'

const getScopetab = (params: { tab?: string; scopetab?: string }): string => {
  if (SCOPETAB_KEY in params) {
    const scopetab = get(params, SCOPETAB_KEY)

    if (scopetab === undefined) {
      return ''
    }

    return `/${scopetab}`
  }
  return ''
}

export default (tabs?: TabList, options?: RoutedTabsOptions) => {
  const match = useRouteMatch<{ tab: string } | {}>()
  const history = useHistory()
  const tabKey = options?.tabKey ?? 'tab'
  const params = match?.params ?? []
  const tab = tabKey in params ? get(params, tabKey) : undefined
  const tabsPresent = hasTabs(tabs)
  const scopetab = getScopetab(params)
  const formik = useContext(FormikContext)

  let currentTabIndex = -1
  if (tabsPresent) {
    currentTabIndex = 0
    if (tab !== undefined) {
      currentTabIndex = tabs?.findIndex(({ path }) => path === `/${tab}`) ?? -1
    }
  }

  // eslint-disable-next-line prefer-const
  let [value, setValue] = useState(tabsPresent ? (tab !== undefined ? `/${tab}` : '') : null)

  const allowedTabs = options?.tabsWithSecondaryTabs

  // TODO pass whole, ready-made URLs from outside if possible
  const handleTabChange = useCallback(
    (_, value: string) => {
      let sliceSize = 1

      if (!isEmpty(location.search) && !isNil(formik)) {
        formik.setFieldValue('after', formik.initialValues.after)
        formik.setFieldValue('page', formik.initialValues.page)
      }

      if (!isNil(allowedTabs?.tabs) && allowedTabs?.tabs?.includes(tab)) {
        sliceSize = 2
      }

      if (!isNil(allowedTabs?.tabs) && allowedTabs?.tabs?.includes(value.replace('/', ''))) {
        history.replace(
          `${tab !== undefined ? sliceUrl(match?.url, sliceSize) : match?.url}${value}${allowedTabs?.defaultSecondary as string}${
            location.search
          }`
        )
      } else {
        if (scopetab !== '') {
          sliceSize = 2
        }

        history.replace(`${tab !== undefined ? sliceUrl(match?.url, sliceSize) : match?.url}${value}${scopetab}${location.search}`)
      }
      setValue(value)
    },
    [formik, allowedTabs?.tabs, allowedTabs?.defaultSecondary, tab, history, match?.url, scopetab]
  )

  value = !isEmpty(value) ? value : tab !== undefined ? `/${tab}` : ''

  return {
    value,
    prevValue: hasTabs(tabs) && currentTabIndex > 0 ? tabs[currentTabIndex - 1].path : null,
    nextValue: hasTabs(tabs) && currentTabIndex < tabs.length - 1 ? tabs[currentTabIndex + 1].path : null,
    handleTabChange
  }
}
